import { Badge, Tooltip } from 'antd'
import { useState, type ReactNode } from 'react'
import { BiFilterAlt } from 'react-icons/bi'
import { MdClose } from 'react-icons/md'

import { RefreshButton } from '@/Ship/Components/UI/RefreshButton/RefreshButton'
import { cx } from '@/Ship/Helpers/cssClass'
import listStyles from '@/Ship/Templates/CrudResource/CrudResourceTemplate.module.css'

/** Чіп активної умови фільтра: «×» знімає умову */
export interface ReportShellChip {
    key: string
    label: string
    value: string
    onRemove: () => void
}

interface ReportShellProps {
    title: string
    /** Рядок під назвою: період і кількість (напр. «01.09 – 30.09.2026 · 5 задач») */
    subtitle: ReactNode
    /** Смуга фільтра звіту (застосовується одразу) */
    filterPanel: ReactNode
    /** Чіпи активних умов під шапкою */
    chips: ReportShellChip[]
    /** «Скинути все» — фільтри звіту за замовчуванням */
    onClearFilters: () => void
    /** Кнопка «Оновити» в шапці */
    onRefresh: () => void
    refreshing: boolean
    /** Ключ localStorage для стану «фільтр розгорнутий» (з префіксом autostrada., як решта Ship) */
    storageKey: string
    /** Додаткові кнопки шапки перед «Оновити» (напр., експорт) */
    actions?: ReactNode
    children: ReactNode
}

const readOpen = (key: string): boolean => {
    try {
        return window.localStorage.getItem(key) !== 'closed'
    } catch {
        return true
    }
}

const writeOpen = (key: string, open: boolean): void => {
    try {
        window.localStorage.setItem(key, open ? 'open' : 'closed')
    } catch {
        // приватний режим — стан просто не запам'ятається
    }
}

/**
 * Картка звіту в стилі картки колекції (CrudResourceTemplate): шапка з назвою, підзаголовком
 * і кнопками (фільтр із лічильником умов, оновити), смуга фільтра, що згортається, чіпи
 * активних умов зі зняттям по «×» і вміст (підсумки, таблиця). Класи картки, шапки, смуги
 * фільтра й чіпів беруться з модуля списків — щоб звіти виглядали як решта розділів.
 */
export const ReportShell = ({
    title,
    subtitle,
    filterPanel,
    chips,
    onClearFilters,
    onRefresh,
    refreshing,
    storageKey,
    actions,
    children,
}: ReportShellProps) => {
    const [open, setOpen] = useState(() => readOpen(storageKey))

    const toggle = () => {
        setOpen((value) => {
            writeOpen(storageKey, !value)
            return !value
        })
    }

    return (
        <div className={cx(listStyles, 'page')}>
            <section className={cx(listStyles, 'card')}>
                <header className={cx(listStyles, 'header')}>
                    <div className={cx(listStyles, 'headerMain')}>
                        <h2 className={cx(listStyles, 'title')}>{title}</h2>
                        <div className={cx(listStyles, 'subtitle')} aria-live="polite">
                            {subtitle}
                        </div>
                    </div>
                    <div className={cx(listStyles, 'headerActions')}>
                        <Tooltip title={open ? 'Сховати фільтр' : 'Показати фільтр'}>
                            <Badge count={chips.length} size="small" color="#e84923" offset={[-2, 2]}>
                                <button
                                    type="button"
                                    className={`${cx(listStyles, 'iconButton')} ${open ? cx(listStyles, 'iconButtonActive') : ''}`}
                                    aria-pressed={open}
                                    aria-label="Фільтр"
                                    onClick={toggle}
                                >
                                    <BiFilterAlt />
                                </button>
                            </Badge>
                        </Tooltip>
                        {actions}
                        <RefreshButton onRefresh={onRefresh} refreshing={refreshing} />
                    </div>
                </header>

                {/* Згорнута смуга: поза Tab-порядком і поза деревом доступності (inert — як рядок:
                    React 18 не знає булевого атрибута) */}
                <div
                    className={`${cx(listStyles, 'filterWrap')} ${open ? cx(listStyles, 'filterWrapOpen') : ''}`}
                    aria-hidden={!open}
                >
                    <div className={cx(listStyles, 'filterInner')} {...(open ? {} : { inert: '' })}>
                        <div className={cx(listStyles, 'filterBar')}>{filterPanel}</div>
                    </div>
                </div>

                {chips.length > 0 && (
                    <div className={cx(listStyles, 'chips')} role="list" aria-label="Активні фільтри">
                        {chips.map((chip) => (
                            <span key={chip.key} className={cx(listStyles, 'chip')} role="listitem">
                                <span className={cx(listStyles, 'chipLabel')}>{chip.label}:</span>
                                <span className={cx(listStyles, 'chipValue')}>{chip.value}</span>
                                <button
                                    type="button"
                                    className={cx(listStyles, 'chipRemove')}
                                    aria-label={`Зняти фільтр ${chip.label}`}
                                    onClick={chip.onRemove}
                                >
                                    <MdClose />
                                </button>
                            </span>
                        ))}
                        <button type="button" className={cx(listStyles, 'chipsClear')} onClick={onClearFilters}>
                            Скинути все
                        </button>
                    </div>
                )}

                {children}
            </section>
        </div>
    )
}
